import { Report } from 'notiflix'
import { apiDownload } from '../../../helpers/apiRequester'

/**
 * category / letter_type_category: one row per claim or email. thread_outcome: one row per thread, keyed by where it stands.
 * thread_subcategory: threads labelled by the deep dive under one outcome, with sub-category, suggested action and quoted evidence.
 */
export type ExportKind = 'category' | 'letter_type_category' | 'thread_outcome' | 'thread_subcategory'
/** Optional analytics sheet in front of the rows: one line per lender, claim reference or client. */
export type ExportGroupBy = 'lender' | 'claim' | 'client'

export interface ExportRequest {
    kind: ExportKind
    category?: string | null
    sendKind?: string | null
    mailbox?: string | null
    from?: string | null
    to?: string | null
    includeArchived?: boolean
    /** Only archived emails (the Archived tab). */
    archivedOnly?: boolean
    /** Default true: emails not yet linked to a claim appear as one row each. */
    includeUnlinked?: boolean
    /** One row per email (default: one per claim with its latest email, plus one per email without a claim). */
    rowPerEmail?: boolean
    groupBy?: ExportGroupBy | null
    /** thread_subcategory only: narrow to one sub-category, one evidence state, or flagged threads. */
    subcategoryId?: string | null
    subcategoryLabel?: string | null
    evidenceState?: 'verified' | 'needs_check' | 'human' | null
    flaggedOnly?: boolean
}

const fileNameFrom = (disposition: string | null, fallback: string) => {
    const m = disposition?.match(/filename="?([^";]+)"?/)
    return m?.[1] || fallback
}

const saveBlob = (blob: Blob, name: string) => {
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = name
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

/**
 * Download one comms export (.xlsx) from POST comms/inbox/export and save it,
 * reporting success or the server's message. Shared by the Categories page and
 * the mailbox overview so both produce the same file for the same slice.
 */
export const downloadCommsExport = async (request: ExportRequest, fallbackName = 'comms-export.xlsx') => {
    try {
        const resp = await apiDownload('comms/inbox/export', { ...request, dashboardOrigin: window.location.origin })
        const blob = resp.data as Blob
        const rows = resp.headers?.['x-row-count']
        const name = fileNameFrom(resp.headers?.['content-disposition'] ?? null, fallbackName)
        saveBlob(blob, name)
        Report.success('Export ready', `${name}${rows ? ` — ${Number(rows).toLocaleString()} row${Number(rows) === 1 ? '' : 's'}` : ''}.`, 'Okay')
        return true
    } catch (err: any) {
        let message = 'Could not build the export.'
        const data = err?.response?.data
        if (data instanceof Blob) {
            try {
                message = JSON.parse(await data.text())?.message || message
            } catch {
                /* keep default */
            }
        } else if (data?.message) message = data.message
        Report.failure('Export failed', message, 'Okay')
        return false
    }
}
